import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import CinematicLoader from '@src/components/CinematicLoader';

type Props = {
  onLogin: (token: string) => void;
};

const COGNITO_AUTH_URL = import.meta.env.VITE_COGNITO_AUTH_URL || '';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const COGNITO_USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const COGNITO_REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/login`;

const userPool =
  COGNITO_USER_POOL_ID && COGNITO_CLIENT_ID
    ? new CognitoUserPool({ UserPoolId: COGNITO_USER_POOL_ID, ClientId: COGNITO_CLIENT_ID })
    : null;

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function exchangeCodeForToken(code: string): Promise<string | null> {
  const verifier = sessionStorage.getItem('pkce_verifier');
  if (!verifier) return null;

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: COGNITO_REDIRECT_URI,
    code,
    code_verifier: verifier,
  });

  try {
    const res = await fetch(`${COGNITO_AUTH_URL}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    if (!res.ok) return null;

    const data = await res.json();
    sessionStorage.removeItem('pkce_verifier');
    return data.id_token || data.access_token || null;
  } catch {
    return null;
  }
}

function buildCognitoUrl(challenge: string, identityProvider?: string): string {
  const endpoint = identityProvider ? 'authorize' : 'login';
  let url =
    `${COGNITO_AUTH_URL}/oauth2/${endpoint}?client_id=${COGNITO_CLIENT_ID}` +
    `&response_type=code&scope=openid+email+profile` +
    `&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}` +
    `&code_challenge=${challenge}&code_challenge_method=S256`;
  if (identityProvider) {
    url += `&identity_provider=${identityProvider}`;
  }
  return url;
}

const LoginPage = ({ onLogin }: Props) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code && COGNITO_AUTH_URL) {
      setExchanging(true);
      window.history.replaceState({}, '', '/login');
      exchangeCodeForToken(code).then(jwt => {
        if (jwt) {
          onLogin(jwt);
        } else {
          setError('Login failed. Please try again.');
          setExchanging(false);
        }
      });
    }
  }, [onLogin]);

  const initiateOAuth = async (identityProvider?: string) => {
    if (!COGNITO_AUTH_URL || !COGNITO_CLIENT_ID) return;
    setLoading(true);
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem('pkce_verifier', verifier);
    window.location.href = buildCognitoUrl(challenge, identityProvider);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !userPool) return;

    setLoading(true);
    setError('');

    const cognitoUser = new CognitoUser({ Username: email.trim(), Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email.trim(), Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: session => {
        const jwt = session.getIdToken().getJwtToken();
        onLogin(jwt);
        setLoading(false);
      },
      onFailure: err => {
        setError(err.message || 'Invalid email or password');
        setLoading(false);
      },
    });
  };

  if (exchanging) {
    return <CinematicLoader message="Completing authentication" submessage="Please wait while we process your login" />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="bg-brand-500 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-bold text-white shadow-lg">
            R
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Resume Applier</h1>
          <p className="mt-2 text-gray-500">AI-Powered Resume Tailoring Dashboard</p>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm">
          {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          {COGNITO_AUTH_URL && (
            <>
              <button
                onClick={() => initiateOAuth('Google')}
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>

              <div className="my-6 flex items-center">
                <div className="flex-1 border-t" />
                <span className="px-4 text-sm text-gray-400">or sign in with email</span>
                <div className="flex-1 border-t" />
              </div>
            </>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-1"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email.trim() || !password.trim()}
              className="bg-brand-500 hover:bg-brand-600 w-full rounded-lg px-4 py-3 font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/register" className="text-brand-600 hover:text-brand-700 text-sm">
              Don't have an account? Sign Up
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400">Get the Chrome extension to auto-apply to jobs</p>
      </div>
    </div>
  );
};

export default LoginPage;
